import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { translations, SUPPORTED, FALLBACK } from "./translations";

const STORAGE_KEY = "gag_lang";
const I18nContext = createContext(null);

export const detectLanguage = () => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (SUPPORTED.includes(stored)) return stored;
  const nav = (navigator.languages?.[0] || navigator.language || "").toLowerCase();
  if (nav.startsWith("en")) return "en";
  return FALLBACK;
};

const lookup = (lang, key) => key.split(".").reduce((o, k) => (o ? o[k] : undefined), translations[lang]);

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectLanguage);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!SUPPORTED.includes(next)) return;
    localStorage.setItem(STORAGE_KEY, next);
    setLangState(next);
  }, []);

  const t = useCallback(
    (key, vars) => {
      let str = lookup(lang, key) ?? lookup(FALLBACK, key) ?? key;
      if (vars && typeof str === "string") {
        Object.entries(vars).forEach(([k, v]) => { str = str.replace(`{{${k}}}`, v); });
      }
      return str;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
